<template>
  <dialog
    ref="dialog"
    aria-labelledby="go-live-title"
    aria-describedby="go-live-explanation"
    class="go-live-dialog shadow-xxl fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[520px] overflow-y-auto rounded-3xl bg-white p-0 text-neutral-900 backdrop:bg-neutral-900/60 backdrop:backdrop-blur-sm"
    @cancel="cancel"
    @close="restoreFocus"
    @click="onBackdrop"
  >
    <div v-if="message" :data-message-id="message.id">
      <div
        class="relative isolate overflow-hidden px-7 pt-7 pb-8 text-center sm:px-10"
        :class="message.palette"
      >
        <div
          class="scene-pattern pointer-events-none absolute inset-0 -z-10"
          :class="`scene-${message.pattern}`"
          aria-hidden="true"
        />
        <button
          type="button"
          aria-label="Close go live confirmation"
          class="absolute top-4 right-4 rounded-full p-2 opacity-70 transition hover:bg-white/20 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2"
          :disabled="store.isSaving"
          @click="cancel"
        >
          <XIcon class="size-5" />
        </button>
        <div
          class="scene-art relative mx-auto mt-4 mb-5 flex h-28 w-44 items-center justify-center"
          aria-hidden="true"
        >
          <span class="scene-spark absolute top-1 left-1 text-xl">✦</span>
          <span class="scene-symbol block text-[84px] leading-none">
            {{ message.symbol }}
          </span>
          <span class="scene-spark absolute right-0 bottom-1 text-3xl">✧</span>
        </div>
        <h2
          id="go-live-title"
          class="relative text-[29px] leading-tight font-semibold tracking-tight sm:text-[32px]"
          style="text-wrap: balance"
        >
          {{ message.title }}
        </h2>
        <p
          class="relative mx-auto mt-3 max-w-sm text-sm leading-relaxed opacity-85"
        >
          {{ message.joke }}
        </p>
      </div>
      <div class="p-6 sm:p-8">
        <h3 class="text-lg font-semibold">Ready to talk to someone now?</h3>
        <p
          id="go-live-explanation"
          class="mt-2 text-sm leading-relaxed text-neutral-600"
        >
          Only go live if you can take a call. Keep GrowthMentor open and be
          ready to answer when it rings.
        </p>
        <dl
          class="my-6 grid grid-cols-3 divide-x divide-neutral-200 rounded-xl bg-neutral-50 py-4 text-center"
        >
          <div class="flex flex-col-reverse">
            <dt class="mt-1 text-xs text-neutral-500">per call</dt>
            <dd class="text-lg font-semibold">15 min</dd>
          </div>
          <div class="flex flex-col-reverse">
            <dt class="mt-1 text-xs text-neutral-500">to answer</dt>
            <dd class="text-lg font-semibold">90 sec</dd>
          </div>
          <div class="flex flex-col-reverse">
            <dt class="mt-1 text-xs text-neutral-500">visible as live</dt>
            <dd class="text-lg font-semibold">Up to 2h</dd>
          </div>
        </dl>
        <p
          v-if="store.goLiveError"
          role="alert"
          class="bg-error-50 text-error-800 mb-4 rounded-lg p-3 text-sm"
        >
          {{ store.goLiveError }}
        </p>
        <BaseButton
          color="primary"
          size="large"
          is-block
          :is-disabled="store.isSaving"
          class="focus-visible:ring-primary-500 focus-visible:ring-2 focus-visible:ring-offset-2"
          @click="store.confirmGoLive()"
        >
          <RadioIcon class="mr-2 size-4" aria-hidden="true" />
          {{ store.isSaving ? 'Going live…' : 'I’m ready. Go live' }}
        </BaseButton>
        <button
          type="button"
          class="focus-visible:outline-primary-500 mt-3 w-full rounded-lg py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 focus-visible:outline-2 disabled:opacity-50"
          :disabled="store.isSaving"
          @click="cancel"
        >
          Not yet
        </button>
        <p class="mt-2 text-center text-xs text-neutral-500">
          You can stop at any time.
        </p>
      </div>
    </div>
  </dialog>
</template>
<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import { useInstantSessionsStore } from '@/modules/instant-sessions/stores/instantSessions'
import {
  nextGoLiveMessage,
  type GoLiveMessage,
} from '@/modules/instant-sessions/data/goLiveMessages'
import RadioIcon from '~icons/lucide/radio'
import XIcon from '~icons/lucide/x'

const store = useInstantSessionsStore()
const dialog = useTemplateRef<HTMLDialogElement>('dialog')
const message = ref<GoLiveMessage | null>(null)
let trigger: HTMLElement | null = null
let previousOverflow = ''
watch(
  () => store.goLiveConfirmationOpen,
  async (open) => {
    if (!open) {
      dialog.value?.close()
      return
    }
    trigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    message.value = nextGoLiveMessage()
    await nextTick()
    if (!store.goLiveConfirmationOpen || !dialog.value) return
    previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialog.value.showModal()
  },
)
function cancel(event?: Event) {
  event?.preventDefault()
  if (!store.isSaving) store.cancelGoLive()
}
function onBackdrop(event: MouseEvent) {
  const element = dialog.value
  if (!element || event.target !== element) return
  const rect = element.getBoundingClientRect()
  if (
    event.clientX < rect.left ||
    event.clientX > rect.right ||
    event.clientY < rect.top ||
    event.clientY > rect.bottom
  )
    cancel()
}
function restoreFocus() {
  document.body.style.overflow = previousOverflow
  if (trigger?.isConnected) trigger.focus()
}
onBeforeUnmount(() => {
  if (dialog.value?.open) restoreFocus()
  store.cancelGoLive()
})
</script>
<style scoped>
.scene-pattern {
  opacity: 0.1;
}
.scene-orbit {
  background: repeating-radial-gradient(
    circle at 50% 29%,
    transparent 0 33px,
    currentColor 34px 35px,
    transparent 36px 64px
  );
}
.scene-grid {
  background-image:
    linear-gradient(currentColor 1px, transparent 1px),
    linear-gradient(90deg, currentColor 1px, transparent 1px);
  background-size: 32px 32px;
  transform: rotate(-9deg) scale(1.3);
}
.scene-waves {
  background: repeating-radial-gradient(
    ellipse at 0 0,
    transparent 0 18px,
    currentColor 19px 20px,
    transparent 21px 39px
  );
}
.scene-rays {
  background: repeating-conic-gradient(
    from 0deg at 50% 25%,
    transparent 0deg 16deg,
    currentColor 16deg 17deg,
    transparent 17deg 30deg
  );
}
.scene-confetti {
  background-image:
    radial-gradient(circle, currentColor 1.5px, transparent 2px),
    radial-gradient(circle, currentColor 2px, transparent 2.5px);
  background-position:
    0 0,
    23px 29px;
  background-size:
    49px 49px,
    71px 71px;
}
.scene-symbol {
  transform: rotate(-9deg);
  filter: drop-shadow(0 8px 5px rgb(0 0 0 / 0.12));
}
.scene-spark {
  transform: rotate(12deg);
}
@media (prefers-reduced-motion: no-preference) {
  .go-live-dialog[open] {
    animation: arrive 180ms ease-out;
  }
  .go-live-dialog[open] .scene-symbol {
    animation: bob-in 400ms ease-out;
  }
}
@keyframes arrive {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes bob-in {
  from {
    opacity: 0;
    transform: translateY(12px) rotate(-18deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(-9deg);
  }
}
</style>
